import {
  PostApplicationRequestBody,
  PutApplicationRequestBody,
  fetchPostApplication,
  fetchPostState,
  fetchPostThreatmetrixEvent,
  fetchPutApplication,
  fetchPutState,
  fetchPutThreatmetrixEvent,
} from '@iwoca/lapi-client/edge';
import { API_AUTH_TOKEN } from '@iwoca/lapi-client/lendingAPI';
import { useMutation } from '@tanstack/react-query';

import { TPutStateRequestBodyDE } from '../../lapiOverrideTypesDE';
import { IwQueryClient } from '../../store/IwQueryClient';
import { TPostStateRequestBody, TPutStateRequestBody } from '../../types';

export function usePostApplicationMutation() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({
      body,
      stateKey,
    }: {
      body: PostApplicationRequestBody;
      stateKey: string;
    }) => {
      await fetchPostApplication({
        body,
        stateKey,
      });
    },
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetApplication'],
      });
    },
  });

  return {
    mutatePostApplication: mutateAsync,
  };
}

export function usePutApplicationMutation() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({
      body,
      stateKey,
    }: {
      body: PutApplicationRequestBody;
      stateKey: string;
    }) => {
      await fetchPutApplication({
        body,
        stateKey,
      });
    },
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetApplication'],
      });
    },
  });

  return {
    mutatePutApplication: mutateAsync,
  };
}

export function usePostStateMutation() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({ body }: { body: TPostStateRequestBody }) => {
      const stateResponse = await fetchPostState({
        headers: {
          authorization: API_AUTH_TOKEN,
        },
        body,
      });

      return stateResponse.data?.state_key;
    },
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetProfile'],
      });
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetContext'],
      });
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetState'],
      });
    },
  });

  return {
    mutatePostState: mutateAsync,
  };
}

// The Cognito-first signup endpoint. Semantic sibling of POST /state/, but
// creates the CognitoUserMapping directly from the iwoca-user-id header
// (injected by envoy after auth-service /signup mints the cookie) rather than
// reconciling by email. Response shape drops in with /state/.
// TODO: replace this hand-rolled fetch with fetchPostSignup once
// @iwoca/lapi-client publishes the new endpoint (backend: iwoca-django!67812).
export function usePostSignupMutation() {
  const { mutateAsync } = useMutation({
    mutationFn: async ({ body }: { body: TPostStateRequestBody }) => {
      const response = await fetch('/api/lending/edge/signup/', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          authorization: API_AUTH_TOKEN,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Match LapiError semantics: the response body is the error message,
        // so callers like parseErrorMessageFromPointerUK can JSON.parse it.
        throw new Error(await response.text());
      }

      const stateResponse = (await response.json()) as {
        data?: { state_key?: string };
      };
      return stateResponse.data?.state_key;
    },
    onSuccess: () => {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetProfile'],
      });
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetContext'],
      });
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetState'],
      });
    },
  });

  return {
    mutatePostSignup: mutateAsync,
  };
}

export async function fetchPutAndGetState({
  body,
  stateKey,
}: {
  body: TPutStateRequestBody | TPutStateRequestBodyDE;
  stateKey: string;
}) {
  await fetchPutState({
    //@ts-expect-error LAPI doesn't handle DE typing
    body,
    stateKey,
  });

  await IwQueryClient.getOrCreate().invalidateQueries({
    queryKey: ['fetchGetState'],
  });
}

export function usePostThreatMetrixMutation() {
  const { data, mutateAsync } = useMutation({
    mutationFn: async ({ stateKey }: { stateKey: string }) => {
      const response = await fetchPostThreatmetrixEvent({
        body: { data: { page_id: 1 } },
        stateKey,
      });

      return {
        tmSessionId: response.data.tm_session_id,
      };
    },
  });

  return {
    tmSessionId: data?.tmSessionId,
    mutatePostThreatMetrix: mutateAsync,
  };
}

export function usePutThreatMetrixMutation() {
  const { mutate } = useMutation({
    mutationFn: async ({
      tmSessionId,
      stateKey,
    }: {
      tmSessionId: string;
      stateKey: string;
    }) => {
      await fetchPutThreatmetrixEvent({
        body: { data: { is_profiled: true } },
        stateKey,
        tmSessionId,
      });
    },
  });

  return {
    mutatePutThreatMetrix: mutate,
  };
}
